import axios from "axios";
import { ENV } from "../configs/environment";
import { getCurrentLocale } from "../utlis/locale";
import v5Api from "./v5Api";

const activityMonitoringApi = axios.create({
  baseURL: ENV.ACTIVITY_MONITORING_API_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

const getViewerTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const toApiFailure = (error) => {
  const body = error.response?.data;
  return {
    success: false,
    status: body?.status ?? 0,
    data: null,
    message: body?.message || error.message || "Request failed",
    error: body?.error || null,
    httpStatus: error.response?.status,
  };
};

// Backend copy and daily buckets must use the same locale/timezone currently
// shown by the app. X-Timezone is an IANA zone such as Asia/Kolkata.
activityMonitoringApi.interceptors.request.use(
  (config) => {
    config.headers["Accept-Language"] = getCurrentLocale();
    config.headers["X-Timezone"] = getViewerTimezone();
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Get sensor mapping for a senior
 * @param {string} seniorId - The senior UUID
 * @returns {Promise} API response containing sensorId if mapped
 */
export const getSensorMapping = async (seniorId) => {
  try {
    const response = await v5Api.get(`/v5/sensor/mapping?seniorId=${seniorId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching sensor mapping:", error);
    return toApiFailure(error);
  }
};

/**
 * Assign a sensor to a senior
 * @param {string} seniorId - The senior UUID
 * @param {string} sensorId - The sensor ID to assign
 * @param {{pFall?: boolean, nightWandering?: boolean}} [options]
 * @returns {Promise} API response
 */
export const assignSensorToSenior = async (
  seniorId,
  sensorId,
  options = {},
) => {
  try {
    const body = { sensorId };
    if (typeof options.pFall === "boolean") body.pFall = options.pFall;
    if (typeof options.nightWandering === "boolean") {
      body.nightWandering = options.nightWandering;
    }
    const response = await v5Api.post(
      `/v5/sensor/mapping?seniorId=${seniorId}`,
      body,
    );
    return response.data;
  } catch (error) {
    console.error("Error assigning sensor:", error);
    return toApiFailure(error);
  }
};

/**
 * Remove the sensor mapping for a senior.
 * @param {string} seniorId - The senior UUID
 * @returns {Promise} API response
 */
export const unassignSensorFromSenior = async (seniorId) => {
  try {
    const response = await v5Api.delete(
      `/v5/sensor/mapping?seniorId=${seniorId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error removing sensor mapping:", error);
    return toApiFailure(error);
  }
};

/**
 * Get the normalized Fall & Activity Monitoring payload for a resident.
 *
 * Returns the full ActivityMonitorData contract consumed by the Ponto UI:
 * resident, sensor (+details), currentStatus/Alert/Abnormality, alertHistory,
 * trend, activityInsight, abnormalityPattern, summaryText, fallIncidents,
 * fallSummaryText. Backend owns all parsing, lifecycle, dedup, abnormality and
 * trend computation, and LLM summaries. See docs/api-contracts/activity-monitoring.md.
 *
 * @param {string} seniorId - The senior UUID
 * @returns {Promise<{success:boolean, data:object}>}
 */
export const getActivityMonitorData = async (seniorId) => {
  const response = await activityMonitoringApi.get(
    `/api/v1/activity-monitoring/residents/${seniorId}/activity-monitor`,
  );
  return response.data;
};

/**
 * Get backend-bucketed activity trend for a resident over a date range.
 * Frontend never aggregates — the backend returns one bucket per interval.
 *
 * @param {string} seniorId - The senior UUID
 * @param {string} [from] - ISO date (inclusive)
 * @param {string} [to] - ISO date (inclusive)
 * @returns {Promise<{success:boolean, data:object}>}
 */
export const getActivityTrendData = async (seniorId, from, to) => {
  const response = await activityMonitoringApi.get(
    `/api/v1/activity-monitoring/residents/${seniorId}/activity-trend`,
    { params: { from, to } },
  );
  return response.data;
};

export default activityMonitoringApi;
